import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSessionStore } from '@features/auth/store/useSessionStore';
import useDashboardApi from './useDashboardApi';
import { logError } from '@lib/logger/logError';
import { logWarning } from '@lib/logger/logWarning';
import type { ReportType } from '../components/ReportDownloadModal';
import type {
  SessionDetails,
  SessionDetailsCall,
  SessionStats,
  SessionRecordings,
  LoadingState,
  SessionMetricsSummary,
  SessionContactSummary,
} from '../types/dashboard.types';
import { useTranslation } from 'react-i18next';

export type CallsFilter = 'all' | 'connected' | 'meetings' | 'recordings';

const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 350;

export interface UseSessionDetailsViewModelReturn {
  // Data
  sessionDetails: SessionDetails | null;
  filteredCalls: SessionDetailsCall[];
  stats: SessionStats;
  selectedRecordings: SessionRecordings | null;
  /** Distinct contacts this session dialled. */
  contactsCalled: number | null;
  /** How many contacts the session's list holds. */
  listContacts: number | null;
  /** Flagged contacts among the ones dialled. */
  fuegoLeadsCount: number | null;

  // Filters
  callsFilter: CallsFilter;
  setCallsFilter: (filter: CallsFilter) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  dispositionFilter: string;
  setDispositionFilter: (disposition: string) => void;
  agentFilter: string;
  setAgentFilter: (agent: string) => void;
  actionFilter: string;
  setActionFilter: (action: string) => void;
  setOnClickFun: (type: string, filter: string) => void;

  // Server-side pagination
  currentPage: number;
  setCurrentPage: (page: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (rows: number) => void;
  totalCalls: number;
  totalPages: number;

  // Loading
  loadingState: LoadingState;
  /** A refetch over an already-visible session — never the first load. */
  isRefreshing: boolean;
  recordingsLoadingState: LoadingState;

  // Computed
  uniqueDispositions: string[];
  uniqueAgents: Array<{ id: string; name: string }>;
  uniqueActions: string[];

  // Actions
  loadRecordings: (callId: string) => Promise<void>;
  closeRecordings: () => void;
  goBack: () => void;
  downloadReport: (type: ReportType) => Promise<void>;
  isDownloadingReport: boolean;
}

/**
 * Maps the backend summary onto SessionStats.
 *
 * Everything is a straight assignment: the backend computes each figure from
 * `callLogs` through the same canonical pipeline the overview uses, so the two
 * views agree by construction. This function used to derive the headline
 * numbers itself — a rate here, a `Math.max` there — which is exactly how the
 * session detail and the overview drifted apart.
 */
function mapSummaryToStats (summary: SessionMetricsSummary): SessionStats {
  const { dispositionMetrics } = summary;

  return {
    totalCalls: summary.totalCalls,
    uniqueContacts: summary.uniqueContacts,
    connected: summary.connected,
    unclassified: summary.unclassified,
    transferred: summary.transferredCalls,
    dialToConnectRatio: summary.connectRate,
    gatekeeperConversation: dispositionMetrics.gatekeeper_conversation,
    leftVoicemail: dispositionMetrics.left_vm,
    wrongNumber: dispositionMetrics.wrong_number,
    phoneTreeNoConnection: dispositionMetrics.phone_tree_no_connection,
    noLongerWithCompany: dispositionMetrics.no_longer_with_company,
    referredAnotherContact: dispositionMetrics.referred_another_contact,
    decisionMakerConversation: dispositionMetrics.decision_maker_conversation,
    optOut: dispositionMetrics.opt_out,
    noAnswer: dispositionMetrics.no_answer,
    badNumberBusySignal: dispositionMetrics.bad_number_busy_signal,
    phoneRefusal: dispositionMetrics.phone_refusal,
    refuseTransfer: dispositionMetrics.refuse_transfer,
    setAppointments: dispositionMetrics.set_appointment,
    dispositionCounts: dispositionMetrics as unknown as Record<string, number>,
  };
}

export default function useSessionDetailsViewModel (): UseSessionDetailsViewModelReturn {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const dashboardApi = useDashboardApi();
  const { currentUser } = useSessionStore();
  const currentUserId = currentUser?.userId ?? '';
  const { t } = useTranslation();

  // Data state
  const [sessionDetails, setSessionDetails] = useState<SessionDetails | null>(null);
  const [selectedRecordings, setSelectedRecordings] = useState<SessionRecordings | null>(null);
  const [backendStats, setBackendStats] = useState<SessionStats | null>(null);

  // Filter state
  const [callsFilter, setCallsFilter] = useState<CallsFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  // The search box now hits the server, so it is debounced: without this every
  // keystroke would be a request.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dispositionFilter, setDispositionFilter] = useState('all');
  const [agentFilter, setAgentFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');

  // Loading state
  const [loadingState, setLoadingState] = useState<LoadingState>('IDLE');
  // Refreshing an already-visible session, as opposed to loading it for the
  // first time. A ref, not state: it must not trigger a render of its own, and
  // the fetch has to read it without being re-created every time it changes.
  const hasLoadedOnce = useRef(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [recordingsLoadingState, setRecordingsLoadingState] = useState<LoadingState>('IDLE');
  const latestRecordingCallIdRef = useRef<string | null>(null);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [totalCalls, setTotalCalls] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [contactSummary, setContactSummary] = useState<SessionContactSummary | null>(null);

  // Counted by the backend, scoped to the contacts THIS SESSION called.
  //
  // It used to download every contact of the list and count in the browser,
  // which answered a different question: the size of the whole list, and every
  // flagged contact in it, whether or not the session ever dialled them.
  const fetchSessionContactSummary = useCallback(async (callSessionId: string) => {
    try {
      const summary = await dashboardApi.getSessionContactSummaryAsync(callSessionId);
      if (summary.success && summary.data !== undefined) {
        setContactSummary(summary.data);
      }
    } catch (error) {
      await logError('Error fetching session contact summary', 'useSessionDetailsViewModel', { error }, false);
    }
  }, [dashboardApi]);

  // Loads ONE page of calls. Filters, sorting and paging all happen in Mongo:
  // this used to fetch page 1, then loop over every remaining page in batches of
  // 5 to assemble the whole session in memory, and filter it in the browser.
  const fetchSessionDetails = useCallback(async () => {
    if (sessionId === undefined || currentUserId === '') {
      return;
    }

    // Only the FIRST load blanks the page for the skeleton. Every later fetch
    // is a refresh: the session is already on screen and re-rendering the whole
    // skeleton made it flash on every filter click, every page change and every
    // keystroke of the search box.
    if (hasLoadedOnce.current) {
      setIsRefreshing(true);
    } else {
      setLoadingState('LOADING');
    }

    try {
      const response = await dashboardApi.getSessionDetailsAsync(
        sessionId,
        currentUserId,
        currentPage,
        rowsPerPage,
        {
          search: debouncedSearch,
          disposition: dispositionFilter,
          action: actionFilter,
          agentId: agentFilter,
          tab: callsFilter,
        }
      );

      if (!response.success) {
        const errorMessage = response.error?.raw ?? '';

        if (errorMessage.includes('404')) {
          setLoadingState('ERROR');
          void logWarning( t('dashboard.useSessionDetailsViewModel.withoutCalls', 'Call session without calls. No details available.'),
            'useSessionDetailsViewModel',
            { sessionId, error: response.error },
            false
          );

          return;
        }
        setLoadingState('ERROR');
        throw new Error(response.error?.message ?? 'Failed to load session details');
      }

      // API returns { data: { session, calls } } — extract safely from paginated wrapper
      const rawData = response.data?.data;
      const pageData: SessionDetails | null = Array.isArray(rawData)
        ? (rawData[0] ?? null)
        : (rawData as SessionDetails | undefined) ?? null;

      if (pageData?.session == null) {
        setLoadingState('ERROR');
        void logError('Session info is missing', 'useSessionDetailsViewModel', { sessionId }, false);
        return;
      }

      setSessionDetails(pageData);
      setTotalCalls(response.data?.totalCount ?? 0);

      const parsedTotalPages = Number(response.data?.totalPages);
      setTotalPages(Number.isFinite(parsedTotalPages) && parsedTotalPages > 0 ? parsedTotalPages : 1);

      void fetchSessionContactSummary(sessionId);

      // Session-wide stats, independent of the page being viewed.
      const summaryResponse = await dashboardApi.getSessionMetricsSummaryAsync(sessionId);

      // No local fallback: the summary endpoint only 404s for a session with no
      // calls, and such a session fails to load its details in the first place.
      // Recomputing the stats in the browser is what let the two views disagree.
      if (summaryResponse.success && summaryResponse.data !== undefined) {
        setBackendStats(mapSummaryToStats(summaryResponse.data));
      } else {
        setBackendStats(null);
        void logWarning(
          'Session metrics summary unavailable; stat cards will render empty.',
          'useSessionDetailsViewModel',
          { sessionId },
          false
        );
      }

      setLoadingState('SUCCESS');
    } catch (error) {
      setLoadingState('ERROR');
      void logError('Failed to load session details', 'useSessionDetailsViewModel', { error, sessionId }, false);
    } finally {
      // In `finally` so an early return or a throw can never leave the table
      // stuck behind a dimmed overlay.
      hasLoadedOnce.current = true;
      setIsRefreshing(false);
    }
    // Every value this reads must be listed, or the callback keeps its identity
    // and closes over the values it had on first render: the effect below never
    // re-runs, so changing a filter or a page updated the controls and left the
    // table exactly as it was.
  }, [
    sessionId,
    currentUserId,
    currentPage,
    rowsPerPage,
    debouncedSearch,
    dispositionFilter,
    actionFilter,
    agentFilter,
    callsFilter,
    dashboardApi,
    fetchSessionContactSummary,
    t,
  ]);

  // Two separate numbers on purpose. `contactsCalled` is what this session did;
  // `listContacts` is the size of the list it drew from. They used to be one
  // field, which is how the header ended up reporting 112 contacts for a
  // session that called 36.
  const contactsCalled = contactSummary?.contactsCalled ?? null;
  const listContacts = contactSummary?.listContacts ?? null;
  const fuegoLeadsCount = contactSummary?.fuegoLeads ?? null;

  const setOnClickFun = (type: string, filter: string): void => {
    if (type === 'disposition') {
      setDispositionFilter(filter);
    } else {
      setActionFilter(filter);
    }
  };

  // Debounce the search box: it now queries the server, so typing must not fire
  // a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [searchQuery]);

  // Any filter change invalidates the current page number — page 4 of the
  // unfiltered list is meaningless once a filter narrows the result to 2 rows.
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, dispositionFilter, agentFilter, actionFilter, callsFilter, rowsPerPage]);

  // Refetch whenever the page or the filters change. fetchSessionDetails closes
  // over both, so this covers the initial load too.
  useEffect(() => {
    void fetchSessionDetails();
  }, [fetchSessionDetails]);

  // Use backend/fallback stats, with zeroed default as last resort
  const stats = useMemo((): SessionStats => {
    if (backendStats !== null) {
      return backendStats;
    }

    return {
      totalCalls: 0,
      connected: 0,
      uniqueContacts: 0,
      unclassified: 0,
      transferred: 0,
      dialToConnectRatio: 0,
      gatekeeperConversation: 0,
      leftVoicemail: 0,
      wrongNumber: 0,
      phoneTreeNoConnection: 0,
      noLongerWithCompany: 0,
      referredAnotherContact: 0,
      decisionMakerConversation: 0,
      optOut: 0,
      noAnswer: 0,
      badNumberBusySignal: 0,
      phoneRefusal: 0,
      refuseTransfer: 0,
      setAppointments: 0,
      dispositionCounts: {},
    };
  }, [backendStats]);

  // Get unique dispositions for filter
  // The filter dropdowns are session-wide, taken from the aggregate the backend
  // already computes. Deriving them from the loaded rows would have limited each
  // dropdown to whatever happens to be on the page you are looking at.
  const uniqueDispositions = useMemo(() => {
    if (backendStats === null) {
      return [];
    }
    return Object.entries(backendStats.dispositionCounts)
      .filter(([, count]) => count > 0)
      .map(([disposition]) => disposition)
      .sort();
  }, [backendStats]);

  // Comes from the session detail, NOT from the metric event counts. A row
  // shows the single most significant action resolved for its call, so the
  // event names (`called`, `cut_off`, `call_ended`) match no row at all —
  // picking one used to empty the table and look like a broken filter.
  const uniqueActions = useMemo(
    () => sessionDetails?.actions ?? [],
    [sessionDetails?.actions]
  );

  const uniqueAgents = useMemo(
    () => sessionDetails?.agents ?? [],
    [sessionDetails?.agents]
  );

  // Already filtered, sorted and paged by the server.
  const filteredCalls = useMemo(
    () => sessionDetails?.calls ?? [],
    [sessionDetails?.calls]
  );

  // Load recordings
  const loadRecordings = useCallback(async (callId: string, contactId?: string) => {
    if (currentUserId === '') {
      return;
    }

    latestRecordingCallIdRef.current = callId;
    setRecordingsLoadingState('LOADING');
    try {
      const response = await dashboardApi.getRecordingsAsync(callId, currentUserId, contactId);

      // Guard: discard if a newer request was made while this one was in flight
      if (latestRecordingCallIdRef.current !== callId) {
        return;
      }

      if (response.success && response.data !== undefined) {
        setSelectedRecordings(response.data);
        setRecordingsLoadingState('SUCCESS');
      } else {
        setRecordingsLoadingState('ERROR');
      }
    } catch (error) {
      if (latestRecordingCallIdRef.current === callId) {
        setRecordingsLoadingState('ERROR');
      }
      void logError('Failed to load recordings', 'useSessionDetailsViewModel', { error, callId }, false);
    }
  }, [currentUserId, dashboardApi]);

  const closeRecordings = useCallback(() => {
    setSelectedRecordings(null);
    setRecordingsLoadingState('IDLE');
  }, []);

  const goBack = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  const downloadReport = useCallback(async (type: ReportType) => {
    if (sessionId === undefined) {
      return;
    }

    setIsDownloadingReport(true);
    try {
      if (type === 'original') {
        // TODO: Implement original CSV download (requires contactListId lookup)
        // For now, fall back to compact
        await dashboardApi.downloadReportAsync(
          sessionId,
          'compact',
          sessionDetails?.session.organizationId
        );
      } else {
        await dashboardApi.downloadReportAsync(
          sessionId,
          type,
          type === 'raw' ? sessionDetails?.session.organizationId : undefined
        );
      }
    } catch (error) {
      void logError('Failed to download report', 'useSessionDetailsViewModel', { error, sessionId, type }, true);
    } finally {
      setIsDownloadingReport(false);
    }
  }, [sessionId, sessionDetails?.session.organizationId, dashboardApi]);

  return {
    // Data
    sessionDetails,
    filteredCalls,
    stats,
    selectedRecordings,
    contactsCalled,
    listContacts,
    fuegoLeadsCount,

    // Filters
    callsFilter,
    setCallsFilter,
    searchQuery,
    setSearchQuery,
    dispositionFilter,
    setDispositionFilter,
    agentFilter,
    setAgentFilter,
    actionFilter,
    setActionFilter,
    setOnClickFun,

    // Server-side pagination
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    totalCalls,
    totalPages,

    // Loading
    loadingState,
    isRefreshing,
    recordingsLoadingState,

    // Computed
    uniqueDispositions,
    uniqueAgents,
    uniqueActions,

    // Actions
    loadRecordings,
    closeRecordings,
    goBack,
    downloadReport,
    isDownloadingReport,
  };
}
